import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Printer, ArrowLeft, Loader2, ShoppingBag, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

const STATUS_COLORS = { paid: "bg-green-100 text-green-700", created: "bg-amber-100 text-amber-700", failed: "bg-red-100 text-red-700" };

export default function AdminOrders() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { if (user === false) navigate("/admin", { replace: true }); }, [user, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/orders"); setOrders(data); }
    catch { toast.error("Failed to load orders"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (user && user.email) load(); }, [user, load]);

  if (!user || !user.email) return <div className="min-h-screen grid place-items-center bg-slate-50"><Loader2 className="animate-spin text-primary" /></div>;

  const paidTotal = orders.filter((o) => o.status === "paid").reduce((s, o) => s + (o.amount || 0), 0);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 flex h-16 items-center justify-between">
          <div className="flex items-center gap-2.5"><span className="grid h-9 w-9 place-items-center rounded-md bg-primary text-white"><ShoppingBag size={20} /></span><h1 className="font-heading text-base font-extrabold text-slate-900">Online Orders</h1></div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="gap-2" onClick={load}><RefreshCw size={15} /> Refresh</Button>
            <Link to="/admin/dashboard"><Button variant="outline" size="sm" className="gap-2"><ArrowLeft size={15} /> Dashboard</Button></Link>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-4 sm:px-6 py-8">
        <div className="mb-5 rounded-xl border border-slate-200 bg-white p-4 inline-block">
          <span className="text-xs font-semibold uppercase text-slate-500">Total collected (paid)</span>
          <p className="text-2xl font-extrabold text-green-700">₹{paidTotal}</p>
        </div>
        {loading ? (
          <div className="grid place-items-center py-20"><Loader2 className="animate-spin text-primary" /></div>
        ) : orders.length === 0 ? (
          <div data-testid="no-orders" className="rounded-2xl border border-dashed border-slate-300 bg-white py-20 text-center text-slate-400"><ShoppingBag size={40} className="mx-auto" /><p className="mt-3">No orders yet</p></div>
        ) : (
          <div className="space-y-3">
            {orders.map((o) => (
              <div key={o.id} data-testid={`order-${o.id}`} className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-3">
                    <span className="font-bold text-slate-900">{o.customer?.name}</span>
                    <span className="text-sm text-slate-500">{o.customer?.phone}</span>
                    <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${STATUS_COLORS[o.status] || "bg-slate-100 text-slate-600"}`}>{o.status}</span>
                  </div>
                  <span className="text-lg font-extrabold text-slate-900">₹{o.amount}</span>
                </div>
                <ul className="mt-2 text-sm text-slate-600 list-disc pl-5">
                  {(o.items || []).map((i, idx) => <li key={idx}>{i.name} × {i.qty} — ₹{i.subtotal}</li>)}
                </ul>
                <p className="mt-1 text-xs text-slate-400">{new Date(o.created_at).toLocaleString("en-IN")}{o.razorpay_payment_id ? ` · Payment: ${o.razorpay_payment_id}` : ""}</p>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
